import Vue from 'vue';
import Router from 'vue-router';
import i18n from '@/i18n';

// Views
import FindLicensingCommunity from '@/views/FindLicensingCommunity';

// Register Page
import Register from './views/registerPages/Register';

import ScreenContextService from '@/api/services/ScreenContextService';

// Wizard
import ContactDetails from './views/registrationWizard/ContactDetails';
import AboutYourPet from './views/registrationWizard/AboutYourPet';
import AddAnotherPet from './views/registrationWizard/AddAnotherPet';

import RegisterBanner from '@/views/RegisterBanner';

import store from '@/store/store';

Vue.use(Router);

function handleContext(contextLoader) {
  return async (to, from, next) => {
    if (!store?.hasModule?.('navigation')) {
      throw new Error('Navigation module required');
    }

    await store.dispatch('navigation/start');

    try {
      to.params.context = await contextLoader(store.getters['getCityId']);
    } catch (error) {
      await store.dispatch('navigation/handleError', error);
      await store.dispatch('notification/notifyFailure', error);
    } finally {
      await store.dispatch('navigation/stop');
      next();
    }
  };
}

const BOULDER_CITY_ID = 3;

const routes = [
  {
    path: '/',
    name: 'BaseURL',
    components: {
      default: Register,
      banner: RegisterBanner,
    },
    children: [
      {
        path: 'select',
        name: 'Register',
        component: Register,
        props: true,
      },
    ],
    props: {
      default: (route) => ({
        addPet: route.query.addPet === 'true',
        context: route.params.context,
      }),
    },
    beforeEnter: handleContext(ScreenContextService.fetchRegistrationScreenContext),
  },
  {
    path: '/contact-details',
    name: 'ContactDetails',
    props: { default: true },
    component: ContactDetails,
  },
  {
    path: '/add-pet',
    name: 'AboutYourPet',
    component: AboutYourPet,
    props: { default: true },
  },
  {
    path: '/add-another-pet',
    name: 'AddAnotherPet',
    component: AddAnotherPet,
    props: { default: true },
  },
  {
    path: '/pets/:uuid/add-photo',
    name: 'AddPetImage',
    beforeEnter: (to) => {
      let addPetImageUrl = `${window.BASE_URL}/signup/register/add-pet-photo/${to.params.uuid}`;
      if (Object.keys(to.query).length > 0) {
        addPetImageUrl += `?${new URLSearchParams(to.query).toString()}`;
      }
      window.location.replace(addPetImageUrl);
    },
  },
  {
    path: '/activate-tag',
    name: 'SymActivateTagRedirect',
    beforeEnter: ({ query }) => {
      const { token, redirect_path } = query;
      let next_url = null;

      try {
        next_url = new URL(window.NEXT_URL);
      } catch (e) {
        if (e instanceof TypeError) {
          next_url = new URL(window.NEXT_URL, window.location.origin);
        }
      }

      if (next_url instanceof URL) {
        if (token) {
          next_url.searchParams.set('token', token);
        }
        if (redirect_path) {
          next_url.searchParams.set('redirect_path', redirect_path);
        }
      }

      if (
        parseInt(store.getters['register/getCityID']) === BOULDER_CITY_ID &&
        ['OTHER', 'COUNTY'].includes(store.getters['register/getVsLocation'])
      ) {
        if (next_url.searchParams.get('redirect_path') === 'licensing') {
          next_url.searchParams.delete('redirect_path');
        }
      }

      window.location.href =
        next_url ?? `${window.NEXT_URL}?${token ? `token=${token}&` : ''}${redirect_path ? `redirect_path=${redirect_path}&` : ''}`;
    },
  },
  {
    path: '/find-your-licensing-community',
    name: 'FindLicensingCommunity',
    component: FindLicensingCommunity,
  },
];

const router = new Router({
  mode: 'history',
  base: window.BASE,
  routes,
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition;
    } else {
      return { x: 0, y: 0 };
    }
  },
});

router.beforeEach((to, from, next) => {
  let pageTitle = '';

  if (i18n.te(`meta.title.${to.name}`)) {
    pageTitle = i18n.t(`meta.title.${to.name}`);
  }

  document.title = pageTitle ? `DocuPet - ${pageTitle}` : 'DocuPet';

  next();
});

export default router;
